import type { WalkthroughStep } from "../types";

/**
 * ID Bench's tour, started by the sample button or by asking for it.
 *
 * The sample behind it is the real published `20ambocr` ambulance, whose
 * `sirenSettings 289563` and `lightSettings 684` are the case a range check
 * exists for — so the findings, the plan and the before/after steps below all
 * have a real row to point at, and none of them is refused, which is why the
 * refusal step describes the rule rather than a card.
 *
 * The `welcome` step announces the sample and is dropped by the island when no
 * sample is loaded, because "not your files" is false over a folder the reader
 * picked themselves.
 *
 * Also reachable without loading a sample, through the permanent "Show me how
 * this works" control in the intake row (`ShowTourButton`), for the reason that
 * control's own header gives. So a step may assume a result is on screen and may
 * not assume that result came from us.
 *
 * These steps RESTATE what the island already says; they absorb nothing. The
 * refusal panel, the skip-list sentences and the "computed by re-scanning"
 * line all stay in the interface, because a limit stated only in a tutorial
 * nobody opened is a limit not stated.
 */
export const steps: WalkthroughStep[] = [
  {
    id: "welcome",
    title: "This is a sample pack",
    body: "A real, published ambulance is loaded — not your files — with a siren id and a light id both out of range. Six stops, then drop your own folder.",
    placement: "center",
  },
  {
    id: "drop",
    title: "Where your folder goes",
    body: "A `resources/` folder, one resource, or the zips you bought. Only `carcols.meta` and `carvariations.meta` are read for ids, and nothing you drop leaves this tab.",
    selector: "[data-walkthrough='idb-drop']",
    placement: "bottom",
  },
  {
    id: "scope",
    title: "Say what your server runs",
    body: "The Siren Setting Limit Adjuster changes the safe siren range and the bone limit, so tick it only if your server loads it. With several resources in a drop, untick any you want left out — each one is then named as not read, here and in every download.",
    selector: "[data-walkthrough='idb-scope']",
    placement: "bottom",
  },
  {
    id: "findings",
    title: "What is wrong, before anything changes",
    body: "Out of range, the engine's unset sentinel, stock GTA's own ids, duplicates, dangling references and sirens over the bone limit — emergency vehicles first, with everything the scan could not read listed beside them.",
    selector: "[data-walkthrough='idb-findings']",
    placement: "top",
  },
  {
    id: "refused",
    title: "Refusals come first",
    body: "An id this tool cannot prove is safe to move is left exactly as it is, with the reason. A siren or light id is refused when any `carvariations.meta` in the scan could not be parsed, because a reference it cannot see is one it cannot update.",
    selector: "[data-walkthrough='idb-refused']",
    placement: "top",
  },
  {
    id: "moves",
    title: "One row per move, and you choose",
    body: "Only the number changes, never a kit's name, so every binding survives. New ids come from the top of each range down, because stock GTA sits at the bottom. The count under the plan is a re-scan of the patched text, not what the plan hoped to fix.",
    selector: "[data-walkthrough='idb-moves']",
    placement: "top",
  },
  {
    id: "download",
    title: "Only the changed files leave",
    body: "The zip holds each changed file beside a `.bak` of the original, a Markdown report and a JSON manifest of every move — plus anything left out or not read. A sample-pack download carries a sample- prefix.",
    selector: "[data-walkthrough='idb-download']",
    placement: "top",
  },
];

export const finishLabel = "Drop my own folder";
